import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Alert,
  IconButton,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import CustomButton from '../../MUI_custom_component/Button/CustomButton';
import ReusableModal from '../../MUI_custom_component/Modal/CustomModal';
import { toast } from 'react-toastify';
import AddressFormModal from './AddressFormModal';
import { reverseGeocodeAction } from '../APIs/action';
import { mapStyles } from '../mapStyles';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import zIndex from '@mui/material/styles/zIndex';

class GoogleMapsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {}

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='body1'
            color='text.secondary'
          >
            Map temporarily unavailable
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            Please refresh the page
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

const LocationMapSelector = ({
  mapCenter,
  markerPosition,
  onMapClick,
  onCurrentLocation,
  onConfirmLocation,
  onCloseModal,
  preloadedAddressData = null,
  handleSaveAddress = () => {},
  zoom = 15,
  isPwa = false,
}) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [map, setMap] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    useState(false);
  const [addressData, setAddressData] = useState(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressFormModalOpen, setAddressFormModalOpen] = useState(false);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      map
    ) {
      try {
        setMap(null);
      } catch (error) {
        setMap(null);
      }
    } else {
      setMap(null);
    }
  }, []);

  const fetchAddressFromCoordinates = useCallback(
    async (lat, lng) => {
      if (
        preloadedAddressData &&
        markerPosition &&
        Array.isArray(markerPosition) &&
        markerPosition.length >= 2 &&
        Math.abs(markerPosition[0] - lat) < 0.0001 &&
        Math.abs(markerPosition[1] - lng) < 0.0001
      ) {
        setAddressData(preloadedAddressData);
        return;
      }

      setLoadingAddress(true);
      try {
        const response = await reverseGeocodeAction(lat, lng);
        if (!response.error && response.data) {
          setAddressData(response.data);
        } else {
          setMapError('Failed to fetch address for the selected location.');
        }
      } catch (error) {
        setMapError('Failed to fetch address for the selected location.');
      } finally {
        setLoadingAddress(false);
      }
    },
    [preloadedAddressData, markerPosition],
  );

  const handleLoadError = useCallback(() => {
    setMapError(
      'Failed to load Google Maps. Please check your internet connection and try again.',
    );
  }, []);

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    onMapClick({ latlng: { lat, lng } });

    fetchAddressFromCoordinates(lat, lng);
  };

  const handleAddressFormModalClose = () => {
    setAddressFormModalOpen(false);
  };

  const handleAddressFormBack = () => {
    setAddressFormModalOpen(false);
  };

  const handleAddressSave = (addressData) => {
    setAddressFormModalOpen(false);
    handleSaveAddress();
    if (onConfirmLocation) {
      onConfirmLocation(addressData);
    }

    // Close the location map selector after address is saved
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const handleLocationSubmit = () => {
    // Open the address form modal
    setAddressFormModalOpen(true);
  };

  const handleCurrentLocationRequest = () => {
    getCurrentLocation(false);
  };

  const getCurrentLocation = (isRetry = false) => {
    if (!navigator.geolocation) {
      if (!isRetry) {
        setMapError('Geolocation is not supported by this browser.');
      }
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout for better reliability
      maximumAge: 0, // Always request fresh location
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const location = {
          latlng: {
            lat: latitude,
            lng: longitude,
          },
        };

        onCurrentLocation(location);
        setLocationPermissionDenied(false);
        setMapError(null); // Clear any previous errors

        fetchAddressFromCoordinates(latitude, longitude);
      },
      (error) => {
        if (isRetry) {
          // For retry scenarios, only show browser settings tip for permission denied
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermissionDenied(true);
            toast.info('Tip: Look for the location icon in your browser\'s address bar to manage location permissions.');
          } else {
            setLocationPermissionDenied(false);
          }
        } else {
          // For initial request, show detailed error messages
          let errorMessage = 'Unable to retrieve your location. ';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access was denied. Please enable location permission in your browser settings and try again.';
              setLocationPermissionDenied(true);
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings and try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please check your internet connection and try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location. Please try again.';
              break;
          }

          setMapError(errorMessage);
        }
      },
      options,
    );
  };

  const handleRetryLocation = () => {
    setLocationPermissionDenied(false);
    setMapError(null);
    // Request location permission again (with retry flag)
    getCurrentLocation(true);
  };

  useEffect(() => {
    if (
      markerPosition &&
      Array.isArray(markerPosition) &&
      markerPosition.length >= 2
    ) {
      fetchAddressFromCoordinates(markerPosition[0], markerPosition[1]);
    }
  }, [markerPosition, fetchAddressFromCoordinates]);

  useEffect(() => {
    if (preloadedAddressData) {
      setAddressData(preloadedAddressData);
    }
  }, [preloadedAddressData]);

  // Check if required props are not provided
  const shouldShowLoadingState =
    !mapCenter ||
    !markerPosition ||
    !Array.isArray(mapCenter) ||
    !Array.isArray(markerPosition);

  if (shouldShowLoadingState) {
    return (
      <Box
        sx={{
          height: isMobile ? '100vh' : '589px',
          width: isMobile ? '100vh' : '618px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          Map data is loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: isMobile ? '100vh' : '589px',
        width: isMobile ? '100%' : '618px',
        position: isMobile ? 'relative' : 'fixed',
        top: isMobile ? 'auto' : '50%',
        left: isMobile ? 'auto' : '50%',
        transform: isMobile ? 'none' : 'translate(-50%, -50%)',
        borderRadius: isMobile ? 0 : '12px',
        overflow: 'hidden',
        zIndex: isMobile ? 'auto' : 1300,
        boxShadow: isMobile ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.24)',
        // Hide LocationMapSelector when AddressFormModal is open on desktop
        display: !isMobile && addressFormModalOpen ? 'none' : 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Close/Back Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          [isMobile ? 'left' : 'right']: 16,
          zIndex: 1002,
        }}
      >
        <IconButton
          onClick={onCloseModal}
          sx={{
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            width: isMobile ? 40 : 36,
            height: isMobile ? 40 : 36,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          {isMobile ? (
            <ArrowBackIcon sx={{ fontSize: 24, color: '#333' }} />
          ) : (
            <CloseIcon sx={{ fontSize: 24, color: '#333' }} />
          )}
        </IconButton>
      </Box>

      {mapError ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Alert
            severity='error'
            sx={{ maxWidth: '400px' }}
          >
            {mapError}
          </Alert>
        </Box>
      ) : (
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          onError={handleLoadError}
          loadingElement={
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography>Loading map...</Typography>
            </Box>
          }
        >
          <GoogleMapsErrorBoundary>
            <GoogleMap
              key='location-map-selector'
              mapContainerStyle={{
                height: '100%',
                width: '100%',
              }}
              center={{
                lat:
                  mapCenter && Array.isArray(mapCenter) && mapCenter.length >= 2
                    ? mapCenter[0]
                    : 0,
                lng:
                  mapCenter && Array.isArray(mapCenter) && mapCenter.length >= 2
                    ? mapCenter[1]
                    : 0,
              }}
              zoom={zoom}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={handleMapClick}
              options={{
                styles: mapStyles,
                disableDefaultUI: true,
                zoomControl: true,
                zoomControlOptions: {
                  position: 9,
                },
              }}
            >
              <Marker
                position={{
                  lat:
                    markerPosition &&
                    Array.isArray(markerPosition) &&
                    markerPosition.length >= 2
                      ? markerPosition[0]
                      : 0,
                  lng:
                    markerPosition &&
                    Array.isArray(markerPosition) &&
                    markerPosition.length >= 2
                      ? markerPosition[1]
                      : 0,
                }}
                icon={
                  window.google && window.google.maps
                    ? {
                        url:
                          'data:image/svg+xml;charset=UTF-8,' +
                          encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="50" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                            <path d="M60 40c-5.52 0-10 4.48-10 10 0 7.5 10 20 10 20s10-12.5 10-20c0-5.52-4.48-10-10-10zm0 13.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="#333333"/>
                          </svg>
                        `),
                        scaledSize: new window.google.maps.Size(120, 120),
                        anchor: new window.google.maps.Point(60, 75),
                      }
                    : undefined
                }
              />
            </GoogleMap>
          </GoogleMapsErrorBoundary>
        </LoadScript>
      )}

      {!mapError && (
        <>
          <Box
            sx={{
              position: 'absolute',
              bottom: 200,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1001,
            }}
          >
            {!isPwa && (
              <CustomButton
                variant='text'
                color='primary'
                label={
                  locationPermissionDenied
                    ? 'Retry Location Access'
                    : 'Use Current Location'
                }
                onClick={() => {
                  if (locationPermissionDenied) {
                    handleRetryLocation();
                  } else {
                    handleCurrentLocationRequest();
                  }
                }}
                sx={{
                  height: '40px',
                  width: '230px',
                  backgroundColor: 'white',
                  marginBottom: '8px',
                }}
                leftIcon={GpsFixedIcon}
              />
            )}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: isMobile ? 0 : 12,
              borderBottomRightRadius: isMobile ? 0 : 12,
              padding: isMobile ? '16px' : '20px',
              boxShadow: isMobile ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none',
              zIndex: 1000,
              height: '188px',
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                }}
              >
                <PlaceOutlinedIcon
                  sx={{ height: '16px', width: '16px', marginRight: '2px' }}
                  color='#666666'
                />
                <Typography variant='h2' sx={{ marginTop: '4px' }}>
                  {addressData?.landmark}
                </Typography>
              </Box>

              {loadingAddress ? (
                <Typography
                  variant='body1'
                  sx={{ fontStyle: 'italic', color: '#666' }}
                >
                  Fetching address...
                </Typography>
              ) : addressData ? (
                <Box>
                  <Typography
                    variant='body1'
                    sx={{ marginTop: '4px' }}
                  >
                    {addressData.formatted_address}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  sx={{ fontStyle: 'italic', color: '#666' }}
                >
                  Select a location on the map to see address details
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CustomButton
                variant='filled'
                color='warning'
                label='Confirm Location'
                fullWidth
                onClick={handleLocationSubmit}
                sx={{ height: '40px', marginTop: '25px', marginBottom: '32px' }}
              />
            </Box>
          </Box>
        </>
      )}

      <ReusableModal
        open={addressFormModalOpen}
        onClose={handleAddressFormModalClose}
        centerAlign={true}
        maxHeight={isMobile ? '100vh' : '589px'}
        topDesktop={'50%'}
        topMobile={'0%'}
        showHeader={false}
        scrollable={false}
        pt={0}
        px={0}
        pb={0}
        maxWidth={isMobile ? '100%' : '618px'}
        hideBackdrop={true}
      >
        <AddressFormModal
          onBack={handleAddressFormBack}
          onSaveAddress={handleAddressSave}
          addressDetails={addressData}
          locationPermissionDenied={locationPermissionDenied}
          handleRetryLocation={handleRetryLocation}
          handleCurrentLocationRequest={handleCurrentLocationRequest}
          coords={
            markerPosition && Array.isArray(markerPosition)
              ? { lat: markerPosition[0], lng: markerPosition[1] }
              : markerPosition
          }
        />
      </ReusableModal>
    </Box>
  );
};

export default LocationMapSelector;
